import React, { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSpring, animated } from '../ReactSpring';
import { NativeProps, withNativeProps } from '../utils/native-props';
// import { useIphonex } from '../utils/use-iphonex';
import { useLockScroll } from '../utils/use-lock-scroll';
import { renderToContainer, GetContainer } from '../utils/render-to-container';
import { mergeProps } from '../utils/with-default-props';
import { useShouldRender } from '../utils/use-should-render';
import { PropagationEvent, withStopPropagation } from '../utils/with-stop-propagation';
import styles from './mask.module.scss';

const opacityRecord = {
  // default: 0.55,
  default: 0.75,
  thin: 0.35,
  thick: 0.75,
};

export type MaskProps = {
  visible?: boolean;
  children?: React.ReactNode;
  onMaskClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  contentAnimated?: boolean;
  destroyOnClose?: boolean;
  forceRender?: boolean;
  disableBodyScroll?: boolean;
  isDialog?: boolean;
  color?: 'black' | 'white';
  opacity?: 'default' | 'thin' | 'thick' | number;
  getContainer?: GetContainer;
  afterShow?: () => void;
  afterClose?: () => void;
  stopPropagation?: PropagationEvent[];
} & NativeProps<'--z-index'>;

const defaultProps = {
  visible: true,
  isDialog: false,
  destroyOnClose: false,
  contentAnimated: false,
  forceRender: false,
  color: 'black',
  opacity: 'default',
  disableBodyScroll: true,
  getContainer: null,
  stopPropagation: ['click'],
};

export const Mask = (p: MaskProps) => {
  const props = mergeProps(defaultProps, p);
  const ref = useRef<HTMLDivElement>(null);
  // const isIphoneX = useIphonex();
  // const contentBottom = useMemo(() => {
  //   return isIphoneX ? 88 : 40;
  // }, [isIphoneX]);
  const contentBottom = 20;
  const maxContentBottom = useMemo(() => {
    if (globalThis && globalThis.window && globalThis.window.screen) {
      return -globalThis?.window?.screen?.height;
    }
    return -1000;
  }, []);

  useLockScroll(ref, props.visible && props.disableBodyScroll);

  const background = useMemo(() => {
    const opacity = opacityRecord[props.opacity] ?? props.opacity;
    // const rgb = props.color === 'white' ? '255, 255, 255' : '0, 0, 0';
    const rgb = props.color === 'white' ? '51, 51, 51' : '0, 0, 0';
    return `rgba(${rgb}, ${opacity})`;
  }, [props.color, props.opacity]);

  const [active, setActive] = useState(props.visible);
  const [contentAnimatedActive, setContentAnimatedActive] = useState(props.visible);

  const { opacity } = useSpring({
    opacity: props.visible ? 1 : 0,
    config: {
      duration: 300,
      precision: 0.01,
      mass: 1,
      tension: 200,
      friction: 30,
    },
    onStart: () => {
      setActive(true);
      if (!props.visible) {
        setContentAnimatedActive(props.visible);
      } else {
        setTimeout(() => {
          setContentAnimatedActive(props.visible);
        }, 100);
      }
    },
    onRest: () => {
      setActive(props.visible);
      if (props.visible) {
        props.afterShow?.();
      } else {
        props.afterClose?.();
      }
    },
  });

  const contentAnimatedProps = useSpring({
    bottom: contentAnimatedActive ? contentBottom : maxContentBottom,
    config: {
      duration: 150,
      tension: 50,
      friction: 6,
    },
  });

  const shouldRender = useShouldRender(active, props.forceRender, props.destroyOnClose);

  const node = withStopPropagation(
    props.stopPropagation,
    withNativeProps(
      props,
      <animated.div
        className={styles.mask}
        ref={ref}
        style={{
          ...props.style,
          background,
          opacity,
          display: active ? 'unset' : 'none',
        }}
      >
        {props.onMaskClick && <div className={styles.mask_aria_button} role='button' onClick={props.onMaskClick} />}
        {props.contentAnimated && (
          <animated.div
            style={{
              bottom: contentAnimatedProps.bottom,
            }}
            className={classNames({
              [styles.normal_content]: true,
              [styles.dialog_content]: props.isDialog,
            })}
          >
            {shouldRender && props.children}
          </animated.div>
        )}
        {!props.contentAnimated && (
          <div
            className={classNames({
              [styles.normal_content]: true,
              [styles.dialog_content]: props.isDialog,
            })}
          >
            {shouldRender && props.children}
          </div>
        )}
      </animated.div>
    )
  );

  return renderToContainer(props.getContainer, node);
};
